import { SUMMIT_LEVEL, type ZoneDefinition } from '@shefira/shared';

interface Tile {
  x: number;
  y: number;
}

/** Whether a tile stands on a summit's top (ADR 0021): only its cave climbs there. */
export function onSummit(zone: ZoneDefinition, tile: Tile): boolean {
  return zone.heights?.[tile.y]?.[tile.x] === String(SUMMIT_LEVEL);
}

/**
 * Where to walk for `goal` from `from`: the goal itself, unless it stands on
 * a summit the walker is not on; then the cave mouth in the valley that
 * climbs to it, named by the summit's own ladder (both exits lead into the
 * same cave). A hint for the quests' arrow, nothing more.
 */
export function wayTo(zone: ZoneDefinition, goal: Tile, from: Tile): Tile {
  if (!onSummit(zone, goal) || onSummit(zone, from)) return goal;
  const middle = (exit: ZoneDefinition['exits'][number]): Tile => ({
    x: exit.x + Math.floor(exit.width / 2),
    y: exit.y,
  });
  const far = (tile: Tile): number => Math.hypot(tile.x - goal.x, tile.y - goal.y);
  const ladder = zone.exits
    .filter((exit) => onSummit(zone, exit))
    .sort((a, b) => far(middle(a)) - far(middle(b)))[0];
  const mouth = zone.exits.find((exit) => exit.to === ladder?.to && !onSummit(zone, exit));
  return mouth === undefined ? goal : middle(mouth);
}
